import { use, useCallback } from "react";

import { mergeClassNames } from "../../utils/classes";
import Icon from "../Icon/Icon";
import { ExpandableSectionContext } from "./ExpandableSectionContext";

import type { KeyboardEvent, PropsWithChildren, ReactNode } from "react";

import * as styles from "./expandableSection.module.scss";

export interface ExpandableSectionHeaderProps {
	headerText?: ReactNode;
	className?: string;
	color?: "normal" | "blue" | "white" | "dark" | "none";
	tabIndex?: number;
	testId?: string;
	borderBottom?: boolean;
}

const getColorClass = (color: "normal" | "blue" | "white" | "dark" | "none" | undefined) => {
	switch (color) {
		case "blue":
			return styles.expandableBlue;
		case "white":
			return styles.expandableWhite;
		case "dark": // SS 2025-12-08 Should be removed, currently mistakenly used as test company top bar.
			return styles.expandableDark;
		case "none":
			return styles.expandableNone;
		default:
			return styles.expandableNormal;
	}
};

export const ExpandableSectionHeader = ({ ...props }: PropsWithChildren<ExpandableSectionHeaderProps>) => {
	const context = use(ExpandableSectionContext);
	if (!context) {
		throw new Error("ExpandableSection.Header must be used within an ExpandableSection");
	}

	const toggleSection = useCallback(() => {
		context.toggle();
	}, [context]);

	const classes = mergeClassNames(
		styles.expandableHeader,
		getColorClass(props.color),
		props.className,
		props.borderBottom && context.expanded && styles.borderBottom,
		!context.expanded && styles.collapsed,
	);

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") {
			toggleSection();
		}
	};

	return (
		<div
			className={classes}
			tabIndex={props.tabIndex ?? 0}
			onKeyDown={handleKeyDown}
			onClick={toggleSection}
			data-testid={props.testId}
		>
			{props.headerText && <span className={styles.header}>{props.headerText}</span>}
			{props.children}
			<Icon
				className={mergeClassNames(styles.icon)}
				name={context.expanded ? "up-open-big" : "down-open-big"}
				size="18"
			/>
		</div>
	);
};
